import { useRef, useEffect } from 'react';

/**
 * Hook personnalisé pour la synthèse vocale (Text-to-Speech)
 * Utilise l'API Web Speech Synthesis du navigateur
 */
const useSpeechSynthesis = () => {
  const synthRef = useRef(null);
  const voicesRef = useRef([]);
  const isSupportedRef = useRef(false);

  useEffect(() => {
    // Vérifier si l'API est supportée
    if ('speechSynthesis' in window) {
      isSupportedRef.current = true;
      synthRef.current = window.speechSynthesis;
      
      // Charger les voix disponibles
      const loadVoices = () => {
        voicesRef.current = synthRef.current.getVoices();
      };
      
      // Les voix peuvent ne pas être disponibles immédiatement
      loadVoices();
      if (synthRef.current.onvoiceschanged !== undefined) {
        synthRef.current.onvoiceschanged = loadVoices;
      }
    }

    // Nettoyage : arrêter toute synthèse en cours lors du démontage
    return () => {
      if (synthRef.current) {
        synthRef.current.cancel();
      }
    };
  }, []);

  /**
   * Parle un texte à voix haute
   * @param {string} text - Le texte à lire
   * @param {object} options - Options de synthèse (lang, pitch, rate, volume)
   */
  const speak = (text, options = {}) => {
    if (!isSupportedRef.current || !synthRef.current) {
      console.warn('La synthèse vocale n\'est pas supportée par votre navigateur.');
      return;
    }

    // Arrêter toute synthèse en cours
    synthRef.current.cancel();

    // Créer une nouvelle utterance
    const utterance = new SpeechSynthesisUtterance(text);
    
    // Configuration par défaut
    utterance.lang = options.lang || 'fr-FR';
    utterance.pitch = options.pitch || 1.0;
    utterance.rate = options.rate || 1.0;
    utterance.volume = options.volume || 1.0;

    // Trouver une voix française si disponible
    if (voicesRef.current.length > 0) {
      const frenchVoice = voicesRef.current.find(
        voice => voice.lang.startsWith('fr')
      );
      if (frenchVoice) {
        utterance.voice = frenchVoice;
      }
    }

    // Gestionnaires d'événements optionnels
    if (options.onStart) {
      utterance.onstart = options.onStart;
    }
    if (options.onEnd) {
      utterance.onend = options.onEnd;
    }
    if (options.onError) {
      utterance.onerror = options.onError;
    }

    // Lancer la synthèse
    synthRef.current.speak(utterance);
  };

  /**
   * Arrête la synthèse vocale en cours
   */
  const stop = () => {
    if (synthRef.current) {
      synthRef.current.cancel();
    }
  };

  /**
   * Vérifie si une synthèse est en cours
   */
  const isSpeaking = () => {
    return synthRef.current && synthRef.current.speaking;
  };

  return {
    speak,
    stop,
    isSpeaking,
    isSupported: isSupportedRef.current,
  };
};

export default useSpeechSynthesis;

